import { gql } from 'apollo-boost'
import * as Fragments from './fragments'

export const ME = gql`
  query {
    me {
      id
      name
      email
      rights
      microsoftConnected
      microsoftAccountEmail
      currentProject {
        id
        name
      }
      activeSubscription {
        id
        plan
        expiresAt
      }
      organization {
        id
        name
        subscription
      }
      organizations {
        id
        name
        rights
      }
    }
    projects {
      id
      name
    }
  }
`

export const ORGANIZATION = gql`
  query {
    organization {
      id
      name
      description
      address
      projects {
        id
        name
      }
    }
  }
`

export const DASHBOARD = gql`
  query {
    me {
      id
      currentProject {
        id
        name
        description
        address
        organization {
          id
          name
          bidders {
            id
            name
          }
        }
        tenders {
          id
          name
          status
          submissionDate
          bidders {
            id
            name
          }
        }
        users {
          id
          user {
            id
            name
          }
        }
      }
    }
  }
`

export const DASHBOARD_OVERVIEW = gql`
  query dashboardOverview($projectId: ID!) {
    dashboardOverview(projectId: $projectId) {
      tenderStats {
        total
        running
        preparation
        awarded
        closed
        byStatus {
          status
          count
        }
      }
      snagStats {
        total
        byStatus {
          status
          count
        }
        byPriority {
          priority
          count
        }
        overdue
      }
      docStats {
        totalDocuments
        newDocumentsLast7Days
        totalTransmittals
      }
      scheduleStats {
        ...ScheduleStatsFragment
      }
      costStats {
        currency
        revisedBudget
        projectedFinalCost
        variance
        pendingCount
      }
      upcomingDeadlines {
        id
        title
        module
        dueDate
        daysRemaining
        status
      }
      recentActivity {
        id
        module
        action
        title
        description
        userName
        createdAt
        tenderId
        snagId
        documentId
        folderId
      }
    }
  }
  ${Fragments.ScheduleStatsFragment}
`

export const PROJECTS = gql`
  query {
    projects {
      id
      name
    }
  }
`

export const PROJECT = gql`
  query project($id: ID!, $role: Role) {
    project(id: $id) {
      id
      name
      code
      description
      address
      language
      resolvedTimezone
      tenders {
        id
        name
        bidders {
          id
          name
        }
      }
      users(role: $role) {
        id
        role
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const TENDERS = gql`
  query tenders($projectId: ID!) {
    tenders(projectId: $projectId) {
      ...TenderFragment
    }
  }
  ${Fragments.TenderFragment}
`

export const USERS = gql`
  query {
    users {
      ...UserFragment
    }
  }
  ${Fragments.UserFragment}
`

// Roster for pickers ("who can I assign this to here"). Reads the project axis,
// which every project role holds — USERS is the organisation roster and denies
// for an EXTERNAL collaborator, leaving the dropdown silently empty.
export const PROJECT_MEMBERS = gql`
  query projectMembers($projectId: ID!) {
    project(id: $projectId) {
      id
      users {
        id
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const TAGS = gql`
  query ($language: String!) {
    tags(language: $language)
  }
`

export const BIDDERS = gql`
  query {
    bidders {
      ...BidderFragment
    }
  }
  ${Fragments.BidderFragment}
`

export const BIDDER_TENDER_MESSAGES = gql`
  query ($id: ID!) {
    bidderTenderMessages(id: $id) {
      ...LogFragment
      mine
      children {
        ...LogFragment
      }
    }
  }
  ${Fragments.LogFragment}
`
export const BIDDER_TENDER = gql`
  query tender($id: ID!) {
    currentBid {
      id
      createdAt
      priceBreakdown {
        id
        children {
          id
          ...PriceBreakdownNodeFragment
        }
      }
      bidder {
        id
        name
      }
      tender {
        id
        round
      }
      files {
        ...FileFragment
      }
    }
    tender(id: $id) {
      id
      name
      description
      status
      commencementDate
      submissionDate
      round
      address
      packageManager {
        id
        name
      }
      files {
        id
        filename
        size
        mimetype
      }
      priceBreakdowns {
        id
        tenderRound
        children {
          ...PriceBreakdownNodeFragment
        }
      }
    }
  }
  ${Fragments.FileFragment}
  ${Fragments.PriceBreakdownNodeFragment}
`

export const TENDER = gql`
  query tender($id: ID!, $round: Int) {
    tender(id: $id) {
      id
      name
      description
      status
      statusActions
      commencementDate
      submissionDate
      address
      hardcopyRequired
      hardcopyAddress
      round
      updatedAt
      logs {
        ...LogFragment
        children {
          ...LogFragment
        }
      }
      packageManager {
        id
      }
      bids(round: $round) {
        id
        round
        totalPrice
        bidder {
          id
          name
          contactName
        }
        files {
          ...FileFragment
        }
        updatedAt
        priceBreakdown {
          id
          children {
            ...PriceBreakdownNodeFragment
          }
        }
      }
      bidders {
        ...BidderFragment
      }
      files {
        ...FileFragment
      }
      priceBreakdowns {
        id
        tenderRound
        children {
          ...PriceBreakdownNodeFragment
        }
        file {
          id
        }
      }
      winnerBidder {
        ...BidderFragment
      }
      organization {
        subscription
      }
    }
  }
  ${Fragments.FileFragment}
  ${Fragments.BidderFragment}
  ${Fragments.LogFragment}
  ${Fragments.PriceBreakdownNodeFragment}
`

export const TENDER_NOTES = gql`
  query ($id: ID!) {
    tender(id: $id) {
      id
      notes
    }
  }
`

export const FILES = gql`
  query files($directory: String) {
    files(directory: $directory) {
      ...FileFragment
    }
  }
  ${Fragments.FileFragment}
`

export const TENDER_FILES = gql`
  query tender($id: ID!) {
    tender(id: $id) {
      id
      files {
        ...FileFragment
      }
    }
  }
  ${Fragments.FileFragment}
`
export const BID_FILES = gql`
  query tender($id: ID!) {
    bid(id: $id) {
      id
      files {
        ...FileFragment
      }
    }
  }
  ${Fragments.FileFragment}
`

export const BIDDER_FOR_TENDER = gql`
  query bidder($tenderId: ID) {
    bidder {
      id
      name
      bids(tenderId: $tenderId) {
        id
        round
        prices
      }
    }
  }
`

export const PRICE_BREAKDOWN = gql`
  query priceBreakdown($id: ID!) {
    priceBreakdown(id: $id) {
      id
      children {
        ...PriceBreakdownNodeFragment
      }
    }
  }
  ${Fragments.PriceBreakdownNodeFragment}
`

export const UNIT_OPTIONS = gql`
  query unitOptions {
    unitOptions {
      value
      label
    }
  }
`

export const TENDER_ACTIVITY_LOGS = gql`
  query tenderActivityLogs($tenderId: ID!) {
    tenderActivityLogs(tenderId: $tenderId) {
      title
      description
      type
      tender {
        name
      }
      bidder {
        name
      }
      user {
        id
        name
      }
      createdAt
    }
  }
`

// ==================== BUILDINGS ====================

export const BUILDINGS = gql`
  query buildings($projectId: ID!) {
    buildings(projectId: $projectId) {
      ...BuildingFragment
      floors {
        ...FloorFragment
      }
    }
  }
  ${Fragments.BuildingFragment}
  ${Fragments.FloorFragment}
`

export const BUILDING = gql`
  query building($id: ID!) {
    building(id: $id) {
      ...BuildingFragment
      floors {
        ...FloorFragment
      }
    }
  }
  ${Fragments.BuildingFragment}
  ${Fragments.FloorFragment}
`

// ==================== FLOORS ====================

export const FLOORS = gql`
  query floors($buildingId: ID!) {
    floors(buildingId: $buildingId) {
      ...FloorFragment
    }
  }
  ${Fragments.FloorFragment}
`

export const FLOORS_BY_PROJECT = gql`
  query floorsByProject($projectId: ID!) {
    floorsByProject(projectId: $projectId) {
      ...FloorFragment
    }
  }
  ${Fragments.FloorFragment}
`

export const FLOOR = gql`
  query floor($id: ID!) {
    floor(id: $id) {
      ...FloorFragment
      snags {
        ...SnagFragment
      }
    }
  }
  ${Fragments.FloorFragment}
  ${Fragments.SnagFragment}
`

// ==================== SNAGS ====================

export const SNAGS = gql`
  query snags($filter: SnagFilterInput, $limit: Int, $offset: Int) {
    snags(filter: $filter, limit: $limit, offset: $offset) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const SNAGS_CONNECTION = gql`
  query snagsConnection($filter: SnagFilterInput, $page: Int, $pageSize: Int) {
    snagsConnection(filter: $filter, page: $page, pageSize: $pageSize) {
      items {
        ...SnagFragment
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        currentPage
        totalPages
        pageSize
      }
    }
  }
  ${Fragments.SnagFragment}
`

export const SNAGS_BY_FLOOR = gql`
  query snagsByFloor($floorId: ID!) {
    snagsByFloor(floorId: $floorId) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const SNAG = gql`
  query snag($id: ID!) {
    snag(id: $id) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const SNAG_STATS = gql`
  query snagStats($filter: SnagFilterInput) {
    snagStats(filter: $filter) {
      total
      byStatus {
        status
        count
      }
      byPriority {
        priority
        count
      }
      byInfrastructure {
        infrastructureType
        count
      }
      overdue
    }
  }
`

// ==================== SNAG SETTINGS ====================

export const SNAG_SETTINGS = gql`
  query snagSettings($projectId: ID!) {
    snagSettings(projectId: $projectId) {
      id
      projectId
      infrastructureTypes
      defaultPriority
      createdAt
      updatedAt
    }
  }
`

// ==================== SNAG ACTIVITY LOGS ====================

export const SNAG_ACTIVITY_LOGS = gql`
  query snagActivityLogs($snagId: ID!) {
    snagActivityLogs(snagId: $snagId) {
      id
      title
      description
      user {
        id
        name
      }
      createdAt
    }
  }
`

// ==================== FILE ====================

// ==================== JOB ====================

export const JOB_STATUS = gql`
  query job($id: ID!) {
    job(id: $id) {
      id
      type
      status
      progress
      currentStep
      totalSteps
      currentStepNum
      error
      createdAt
      updatedAt
      startedAt
      completedAt
      pdfUrl
      pageCount
      fileSize
    }
  }
`

// ============================================
// DOC CONTROL QUERIES
// ============================================

export const DOC_FOLDERS = gql`
  query docFolders($projectId: ID!) {
    docFolders(projectId: $projectId) {
      ...DocFolderFragment
      children {
        ...DocFolderFragment
        children {
          ...DocFolderFragment
        }
      }
    }
  }
  ${Fragments.DocFolderFragment}
`

export const DOC_FOLDER = gql`
  query docFolder($id: ID!) {
    docFolder(id: $id) {
      ...DocFolderFragment
      path {
        id
        name
      }
      permissions {
        ...DocFolderPermissionFragment
      }
    }
  }
  ${Fragments.DocFolderFragment}
  ${Fragments.DocFolderPermissionFragment}
`

export const DOC_FOLDER_TREE = gql`
  query docFolderTree($projectId: ID!) {
    docFolderTree(projectId: $projectId) {
      ...DocFolderFragment
      children {
        ...DocFolderFragment
        children {
          ...DocFolderFragment
          children {
            ...DocFolderFragment
          }
        }
      }
    }
  }
  ${Fragments.DocFolderFragment}
`

export const DOC_DOCUMENTS = gql`
  query docDocuments($folderId: ID!) {
    docDocuments(folderId: $folderId) {
      ...DocDocumentFragment
    }
  }
  ${Fragments.DocDocumentFragment}
`

export const DOC_FOLDER_DOCUMENTS = gql`
  query docFolderDocuments($folderId: ID!, $includeSubfolders: Boolean) {
    docFolderDocuments(folderId: $folderId, includeSubfolders: $includeSubfolders) {
      id
      name
      currentRevision {
        id
        version
        file {
          id
          filename
          size
          mimetype
        }
      }
    }
  }
`

export const DOC_DOCUMENT = gql`
  query docDocument($id: ID!) {
    docDocument(id: $id) {
      ...DocDocumentFragment
      revisions {
        ...DocRevisionFragment
      }
      folder {
        id
        name
        path {
          id
          name
        }
      }
    }
  }
  ${Fragments.DocDocumentFragment}
  ${Fragments.DocRevisionFragment}
`

export const DOC_REVISIONS = gql`
  query docRevisions($documentId: ID!) {
    docRevisions(documentId: $documentId) {
      ...DocRevisionFragment
    }
  }
  ${Fragments.DocRevisionFragment}
`

export const DOC_SHARE_LINKS = gql`
  query docShareLinks($folderId: ID, $documentId: ID) {
    docShareLinks(folderId: $folderId, documentId: $documentId) {
      ...DocShareLinkFragment
    }
  }
  ${Fragments.DocShareLinkFragment}
`

export const DOC_TRANSMITTALS = gql`
  query docTransmittals($status: DocTransmittalStatus, $projectId: ID!) {
    docTransmittals(status: $status, projectId: $projectId) {
      ...DocTransmittalFragment
    }
  }
  ${Fragments.DocTransmittalFragment}
`

export const DOC_TRANSMITTAL = gql`
  query docTransmittal($id: ID!) {
    docTransmittal(id: $id) {
      ...DocTransmittalFragment
      items {
        id
        document {
          id
          name
        }
        revision {
          id
          version
          downloadUrl
          file {
            id
            filename
            mimetype
            size
          }
        }
      }
      recipients {
        id
        userId
        email
        notifiedAt
        viewedAt
        user {
          id
          name
          email
        }
      }
    }
  }
  ${Fragments.DocTransmittalFragment}
`

export const ACCESS_DOC_TRANSMITTAL = gql`
  query accessDocTransmittal($token: String!) {
    accessDocTransmittal(token: $token) {
      success
      error
      transmittal {
        id
        name
        description
        reference
        expiresAt
        items {
          id
          document {
            id
            name
          }
          revision {
            id
            version
            downloadUrl
            file {
              id
              filename
              mimetype
              size
            }
          }
        }
      }
    }
  }
`

export const DOC_ACTIVITY_LOGS = gql`
  query docActivityLogs($filter: DocActivityLogFilter, $limit: Int, $offset: Int) {
    docActivityLogs(filter: $filter, limit: $limit, offset: $offset) {
      ...DocActivityLogFragment
    }
  }
  ${Fragments.DocActivityLogFragment}
`

export const SEARCH_DOC_DOCUMENTS = gql`
  query searchDocDocuments($query: String!, $projectId: ID!) {
    searchDocDocuments(query: $query, projectId: $projectId) {
      ...DocDocumentFragment
      folder {
        id
        name
      }
    }
  }
  ${Fragments.DocDocumentFragment}
`

// Public query - no auth required
export const ACCESS_DOC_SHARE_LINK = gql`
  query accessDocShareLink($token: String!, $password: String) {
    accessDocShareLink(token: $token, password: $password) {
      success
      error
      folder {
        ...DocFolderFragment
      }
      document {
        ...DocDocumentFragment
      }
      subfolders {
        ...DocFolderFragment
      }
      documents {
        ...DocDocumentFragment
      }
    }
  }
  ${Fragments.DocFolderFragment}
  ${Fragments.DocDocumentFragment}
`

// Public query - browse shared folder contents
export const SHARED_FOLDER_CONTENTS = gql`
  query sharedFolderContents($token: String!, $folderId: ID) {
    sharedFolderContents(token: $token, folderId: $folderId) {
      success
      error
      currentFolder {
        ...DocFolderFragment
      }
      parentFolderId
      subfolders {
        ...DocFolderFragment
      }
      documents {
        ...DocDocumentFragment
      }
    }
  }
  ${Fragments.DocFolderFragment}
  ${Fragments.DocDocumentFragment}
`

// ---- AI Evaluation ----

export const AI_PROCESSING_JOB = gql`
  query aiProcessingJob($id: ID!) {
    aiProcessingJob(id: $id) {
      id
      tenderId
      bidId
      type
      status
      progress
      currentStep
      error
      createdAt
      logs { id message level timestamp }
    }
  }
`

export const AI_PROCESSING_JOBS = gql`
  query aiProcessingJobs($tenderId: ID!) {
    aiProcessingJobs(tenderId: $tenderId) {
      id type status bidId progress currentStep error createdAt
    }
  }
`

export const AI_TENDER_ITEMS = gql`
  query aiTenderItems($tenderId: ID!, $page: Int, $pageSize: Int, $search: String) {
    aiTenderItems(tenderId: $tenderId, page: $page, pageSize: $pageSize, search: $search) {
      items { id rowIndex code description unit quantity unitPrice totalPrice category level rowType }
      total
      totalPages
    }
  }
`

export const AI_BID_ITEMS = gql`
  query aiBidItems($bidId: ID!, $page: Int, $pageSize: Int) {
    aiBidItems(bidId: $bidId, page: $page, pageSize: $pageSize) {
      items { id rowIndex code description unit quantity unitPrice totalPrice category rowType matchedTenderCode matchedTenderDescription matchConfidence matchSimilarity mappingId isApproved }
      total
      totalPages
    }
  }
`

export const AI_COMPARISON = gql`
  query aiComparison($tenderId: ID!, $round: Int) {
    aiComparison(tenderId: $tenderId, round: $round) {
      tenderItems { id code description unit quantity unitPrice totalPrice rowType }
      bids {
        bidId
        bidderName
        totalPrice
        coveragePercent
        coverageGroups {
          id groupType confidence label bidTotal tenderTotal priceDifference
          tenderItems { id code description unitPrice totalPrice }
          bidItems { id code description unitPrice totalPrice }
        }
        extraItems { id code description unit quantity unitPrice totalPrice }
        extraItemsTotal
      }
      summary
    }
  }
`

export const AI_TENDER_REPORT = gql`
  query aiTenderReport($tenderId: ID!) {
    aiTenderReport(tenderId: $tenderId) {
      id report reportGeneratedAt summary
    }
  }
`

export const AI_ITEM_MAPPINGS = gql`
  query aiItemMappings($bidId: ID!) {
    aiItemMappings(bidId: $bidId) {
      id similarityScore confidence priceDifference priceRatio isApproved
      tenderItem { id code description unit unitPrice totalPrice }
      bidItem { id code description unit unitPrice totalPrice }
    }
  }
`

export const AI_TENDER_MAPPINGS = gql`
  query aiTenderMappings($tenderId: ID!) {
    aiTenderMappings(tenderId: $tenderId) {
      id tenderItem { id } bidItem { id bidId } confidence similarityScore isApproved
    }
  }
`

export const AI_EXTRACTION_PREVIEW = gql`
  query aiExtractionPreview($id: ID!) {
    aiExtractionPreview(id: $id) {
      id tenderId bidId fileId
      rawAnalysis documentMap userOverrides sampleItems
      status confidence warnings createdAt
    }
  }
`

export const AI_EXTRACTION_PREVIEWS = gql`
  query aiExtractionPreviews($tenderId: ID, $bidId: ID) {
    aiExtractionPreviews(tenderId: $tenderId, bidId: $bidId) {
      id fileId status confidence warnings createdAt
      rawAnalysis sampleItems
    }
  }
`

// ============================================
// MEETINGS
// ============================================

export const MEETINGS = gql`
  query meetings($filter: MeetingFilterInput, $limit: Int, $offset: Int) {
    meetings(filter: $filter, limit: $limit, offset: $offset) {
      ...MeetingFragment
      agendaItems { id }
      actionItems { id status }
    }
  }
  ${Fragments.MeetingFragment}
`

export const MEETINGS_CONNECTION = gql`
  query meetingsConnection($filter: MeetingFilterInput, $page: Int, $pageSize: Int) {
    meetingsConnection(filter: $filter, page: $page, pageSize: $pageSize) {
      items {
        ...MeetingFragment
        agendaItems { id }
        actionItems { id status }
      }
      totalCount
      pageInfo { hasNextPage hasPreviousPage currentPage totalPages pageSize }
    }
  }
  ${Fragments.MeetingFragment}
`

export const MEETING = gql`
  query meeting($id: ID!) {
    meeting(id: $id) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const MEETING_OCCURRENCE = gql`
  query meetingOccurrence($id: ID!) {
    meetingOccurrence(id: $id) {
      ...MeetingOccurrenceFragment
    }
  }
  ${Fragments.MeetingOccurrenceFragment}
`

export const MEETING_STATS = gql`
  query meetingStats($projectId: ID!) {
    meetingStats(projectId: $projectId) {
      total
      scheduled
      completed
      cancelled
      openActionItems
      overdueActionItems
    }
  }
`

export const PROJECT_ACTION_ITEMS = gql`
  query projectActionItems($projectId: ID!, $status: [ActionItemStatus!]) {
    projectActionItems(projectId: $projectId, status: $status) {
      ...ActionItemFragment
      meeting {
        id
        number
        title
        scheduledAt
      }
    }
  }
  ${Fragments.ActionItemFragment}
`

export const ACTION_ITEM_TIMELINE = gql`
  query actionItemTimeline($id: ID!) {
    actionItem(id: $id) {
      ...ActionItemFragment
      occurrenceSnapshots {
        ...ActionItemOccurrenceSnapshotFragment
      }
      updates {
        ...ActionItemUpdateFragment
      }
    }
  }
  ${Fragments.ActionItemFragment}
  ${Fragments.ActionItemOccurrenceSnapshotFragment}
  ${Fragments.ActionItemUpdateFragment}
`

export const MY_PERMISSIONS = gql`
  query MyPermissions {
    myPermissions {
      global { module actions }
      perProject {
        projectId
        projectRole
        modules { module actions overridden }
        recordScopedModules
      }
    }
  }
`

export const USER_MODULE_ACCESS = gql`
  query UserModuleAccess($userId: ID!, $projectId: ID!) {
    userModuleAccess(userId: $userId, projectId: $projectId) {
      id userId projectId module allowed
    }
  }
`

export const PROJECT_USERS_WITH_ACCESS = gql`
  query ProjectUsersWithAccess($projectId: ID!) {
    projectUsersWithAccess(projectId: $projectId) {
      projectUserId
      user {
        id
        name
        email
        company
        rights
      }
      companyName
      projectRole
      projectRoleId
      modules {
        module
        level
        overridden
        custom
        actions
      }
    }
  }
`

export const USER_FOLDER_ACCESS_TREE = gql`
  query UserFolderAccessTree($projectId: ID!, $userId: ID!) {
    userFolderAccessTree(projectId: $projectId, userId: $userId) {
      folderId
      name
      parentId
      restricted
      level
      source
      directLevel
    }
  }
`

export const DOC_FOLDER_SHARING = gql`
  query docFolderSharing($folderId: ID!) {
    docFolder(id: $folderId) {
      id
      name
      restricted
      effectivePermission {
        level
        source
      }
    }
    docFolderEffectivePermissions(folderId: $folderId) {
      userId
      roleId
      level
      inherited
      fromFolderId
      user {
        id
        name
        email
      }
      projectRole {
        id
        name
      }
      fromFolder {
        id
        name
      }
    }
  }
`

export const DOC_FOLDER_MEMBER_ACCESS = gql`
  query docFolderMemberAccess($folderId: ID!) {
    docFolderMemberAccess(folderId: $folderId) {
      userId
      level
      source
      user {
        id
        name
        email
      }
    }
  }
`

export const PROJECT_ROLES = gql`
  query ProjectRoles {
    projectRoles {
      id
      key
      name
      isSystem
      memberCount
      permissions {
        module
        actions
      }
    }
  }
`

// ==================== SUBMITTALS ====================

export const SUBMITTALS = gql`
  query submittals($projectId: ID!, $filter: SubmittalFilterInput, $limit: Int, $offset: Int) {
    submittals(projectId: $projectId, filter: $filter, limit: $limit, offset: $offset) {
      items { ...SubmittalRowFragment }
      totalCount
    }
  }
  ${Fragments.SubmittalRowFragment}
`

export const SUBMITTAL = gql`
  query submittal($id: ID!) {
    submittal(id: $id) { ...SubmittalDetailFragment }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const SUBMITTAL_LOOKUP_VALUES = gql`
  query submittalLookupValues($projectId: ID!, $kind: SubmittalLookupKind, $includeArchived: Boolean) {
    submittalLookupValues(projectId: $projectId, kind: $kind, includeArchived: $includeArchived) {
      id kind value sortOrder archived
    }
  }
`

export const SUBMITTAL_COMPANY_SUGGESTIONS = gql`
  query submittalCompanySuggestions($projectId: ID!) {
    submittalCompanySuggestions(projectId: $projectId)
  }
`

export const SUBMITTAL_ACTIVITY = gql`
  query submittalActivity($submittalId: ID!) {
    submittalActivity(submittalId: $submittalId) {
      id action meta createdAt user { id name }
    }
  }
`

export const SUBMITTAL_DOC_LINKS = gql`
  query submittalDocLinks($documentId: ID!) {
    submittalDocLinks(documentId: $documentId) {
      id
      submittalId
      submittal { id displayNumber status }
    }
  }
`

export const SUBMITTAL_WORKFLOW_TEMPLATES = gql`
  query submittalWorkflowTemplates($projectId: ID!, $includeArchived: Boolean) {
    submittalWorkflowTemplates(projectId: $projectId, includeArchived: $includeArchived) {
      ...SubmittalWorkflowTemplateFragment
    }
  }
  ${Fragments.SubmittalWorkflowTemplateFragment}
`


// ==================== SCHEDULE ====================
export const SCHEDULES = gql`
  query schedules($projectId: ID!, $includeArchived: Boolean) {
    schedules(projectId: $projectId, includeArchived: $includeArchived) {
      ...ScheduleFragment
    }
  }
  ${Fragments.ScheduleFragment}
`
export const SCHEDULE_DETAIL = gql`
  query schedule($id: ID!) {
    schedule(id: $id) {
      schedule {
        ...ScheduleFragment
      }
      activities {
        ...ScheduleActivityFragment
      }
      dependencies {
        ...ScheduleDependencyFragment
      }
      externalActivities {
        ...ScheduleExternalActivityFragment
      }
      calendars {
        ...ScheduleCalendarFragment
      }
      baselines {
        ...ScheduleBaselineFragment
      }
    }
  }
  ${Fragments.ScheduleFragment}
  ${Fragments.ScheduleActivityFragment}
  ${Fragments.ScheduleDependencyFragment}
  ${Fragments.ScheduleExternalActivityFragment}
  ${Fragments.ScheduleCalendarFragment}
  ${Fragments.ScheduleBaselineFragment}
`
export const SCHEDULE_ACTIVITY_LOG = gql`
  query scheduleActivityLog($scheduleId: ID!, $activityId: ID, $take: Int, $skip: Int) {
    scheduleActivityLog(scheduleId: $scheduleId, activityId: $activityId, take: $take, skip: $skip) {
      id
      activityId
      activityName
      action
      meta
      createdAt
      user {
        id
        name
      }
    }
  }
`
export const SCHEDULE_CALENDARS = gql`
  query scheduleCalendars($projectId: ID!) {
    scheduleCalendars(projectId: $projectId) {
      ...ScheduleCalendarFragment
    }
  }
  ${Fragments.ScheduleCalendarFragment}
`
export const SCHEDULE_ACTIVITY_SEARCH = gql`
  query scheduleActivitySearch($projectId: ID!, $query: String!, $excludeScheduleId: ID, $take: Int) {
    scheduleActivitySearch(projectId: $projectId, query: $query, excludeScheduleId: $excludeScheduleId, take: $take) {
      ...ScheduleExternalActivityFragment
    }
  }
  ${Fragments.ScheduleExternalActivityFragment}
`
export const SCHEDULE_SETTINGS = gql`
  query scheduleSettings($projectId: ID!) {
    scheduleSettings(projectId: $projectId) {
      projectId
      slipThresholdDays
    }
  }
`

export const FEEDBACK_ENABLED = gql`
  query feedbackEnabled {
    feedbackEnabled
  }
`

export const COST_CONTROL = gql`
  query costControl($projectId: ID!) {
    costControl(projectId: $projectId) {
      settings {
        ...CostControlSettingsFragment
      }
      nodes {
        id
        parentId
        sortIndex
        kind
        depth
        name
        code
        buildingId
        buildingName
        hasForecastOverride
        costBasedForecast
        estimate
        values {
          ...CostValuesFragment
        }
      }
      total {
        ...CostValuesFragment
      }
      buildings {
        id
        name
      }
      hasInvestorContract
    }
  }
  ${Fragments.CostControlSettingsFragment}
  ${Fragments.CostValuesFragment}
`

export const COST_CONTRACT_VIEW = gql`
  query costContractView($projectId: ID!) {
    costContractView(projectId: $projectId) {
      contracts {
        id
        number
        name
        counterpartyName
        status
        values {
          ...CostContractViewValuesFragment
        }
        items {
          nodeId
          path
          name
          code
          values {
            ...CostContractViewValuesFragment
          }
        }
      }
      uncontracted {
        id
        parentId
        kind
        name
        code
        values {
          ...CostContractViewValuesFragment
        }
      }
      totals {
        ...CostContractViewValuesFragment
      }
      hasInvestorContract
    }
  }
  ${Fragments.CostContractViewValuesFragment}
`

export const COST_BREAKDOWN = gql`
  query costBreakdown($projectId: ID!, $nodeId: ID!, $column: CostColumn!) {
    costBreakdown(projectId: $projectId, nodeId: $nodeId, column: $column) {
      sourceType
      sourceId
      number
      label
      counterparty
      status
      nodeId
      nodeName
      nodeCount
      amount
    }
  }
`

export const COST_CONTRACTS = gql`
  query costContracts($projectId: ID!, $filter: CostContractFilter) {
    costContracts(projectId: $projectId, filter: $filter) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const COST_CURRENCY = gql`
  query costCurrency($projectId: ID!) {
    costCurrency(projectId: $projectId)
  }
`

export const COST_CONTRACT = gql`
  query costContract($id: ID!) {
    costContract(id: $id) {
      contract {
        ...CostContractFragment
        investorValue
      }
      lines {
        id
        nodeId
        nodeName
        nodeCode
        path
        amount
        description
        provisional
      }
      attachments {
        ...CostAttachmentFragment
      }
      priceItems {
        id
        sortIndex
        section
        code
        name
        unit
        quantity
        amount
        discountPercent
        nodeId
        sourceBidItemId
        finalAmount
        provisional
      }
      priceTotals {
        gross
        afterItemDiscounts
        contractDiscount
        total
        unmapped
        unmappedCount
        count
      }
      discountPercent
      discountAmount
      applications {
        ...CostApplicationFragment
      }
      retention {
        percent
        retained
        released
        remaining
        releases {
          id
          date
          amount
          note
          createdByName
          createdAt
        }
      }
      representatives { id userId name email createdAt createdByName }
      access
      submittals { id displayNumber title status pending canOpen }
      provisional { nodeId nodeName path original approvedChanges remaining pending }
    }
  }
  ${Fragments.CostContractFragment}
  ${Fragments.CostAttachmentFragment}
  ${Fragments.CostApplicationFragment}
`

export const COST_ALLOCATION_PREVIEW = gql`
  query costAllocationPreview($projectId: ID!, $amount: Money!, $rootNodeId: ID!) {
    costAllocationPreview(projectId: $projectId, amount: $amount, rootNodeId: $rootNodeId) {
      nodeId
      nodeName
      nodeCode
      path
      amount
    }
  }
`

export const COST_TENDER_AWARDS = gql`
  query costTenderAwards($projectId: ID!) {
    costTenderAwards(projectId: $projectId) {
      tenderId
      tenderName
      bidderId
      bidderName
      bidderEmail
      amount
      contractId
      nodePath
    }
  }
`

export const TENDER_COST_BUDGET = gql`
  query tenderCostBudget($tenderId: ID!) {
    tenderCostBudget(tenderId: $tenderId) {
      canEdit
      node { id name path }
      revisedBudget
      committed
      currency
      bids { bidderId bidderName amount difference differencePercent }
    }
  }
`

export const COST_ACTIVITY_LOG = gql`
  query costActivityLog($projectId: ID!, $entityType: String, $entityTypes: [String!], $entityId: ID, $take: Int, $skip: Int) {
    costActivityLog(projectId: $projectId, entityType: $entityType, entityTypes: $entityTypes, entityId: $entityId, take: $take, skip: $skip) {
      id
      entityType
      entityId
      action
      meta
      subject
      createdAt
      user {
        id
        name
      }
    }
  }
`

export const COST_CHANGES = gql`
  query costChanges($projectId: ID!, $filter: CostChangeFilter) {
    costChanges(projectId: $projectId, filter: $filter) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const COST_CHANGE = gql`
  query costChange($id: ID!) {
    costChange(id: $id) {
      change {
        ...CostChangeFragment
      }
      lines {
        id
        nodeId
        nodeName
        nodeCode
        path
        amount
      }
      attachments {
        ...CostAttachmentFragment
      }
      access
    }
  }
  ${Fragments.CostChangeFragment}
  ${Fragments.CostAttachmentFragment}
`

export const COST_INVOICES = gql`
  query costInvoices($projectId: ID!, $filter: CostInvoiceFilter) {
    costInvoices(projectId: $projectId, filter: $filter) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const COST_INVOICE = gql`
  query costInvoice($id: ID!) {
    costInvoice(id: $id) {
      invoice {
        ...CostInvoiceFragment
      }
      lines {
        id
        nodeId
        nodeName
        nodeCode
        path
        amount
      }
      contractItems {
        nodeId
        nodeName
        nodeCode
        path
        allocated
        invoiced
      }
      attachments {
        ...CostAttachmentFragment
      }
      access
      pendingSubmittals { id displayNumber title status pending canOpen }
    }
  }
  ${Fragments.CostInvoiceFragment}
  ${Fragments.CostAttachmentFragment}
`

export const COST_INVOICE_PREFILL = gql`
  query costInvoicePrefill($contractId: ID!, $amount: Money!) {
    costInvoicePrefill(contractId: $contractId, amount: $amount) {
      nodeId
      nodeName
      nodeCode
      path
      amount
    }
  }
`

export const COST_EXPENSES = gql`
  query costExpenses($projectId: ID!, $filter: CostExpenseFilter) {
    costExpenses(projectId: $projectId, filter: $filter) {
      ...CostExpenseFragment
    }
  }
  ${Fragments.CostExpenseFragment}
`

export const COST_EXPENSE = gql`
  query costExpense($id: ID!) {
    costExpense(id: $id) {
      expense {
        ...CostExpenseFragment
      }
      attachments {
        ...CostAttachmentFragment
      }
    }
  }
  ${Fragments.CostExpenseFragment}
  ${Fragments.CostAttachmentFragment}
`

export const COST_APPLICATION = gql`
  query costApplication($id: ID!) {
    costApplication(id: $id) {
      application {
        ...CostApplicationFragment
      }
      contractKind
      investorCheck {
        nodeId
        investorBilled
        subcontractorBilledOther
      }
      rows {
        key
        kind
        section
        code
        name
        unit
        quantity
        nodeId
        nodePath
        description
        changeNumber
        changeTitle
        value
        previous
        requested
        certified
        note
      }
      retentionPercent
      contractValue
      latest
      attachments {
        ...CostAttachmentFragment
      }
      access
      pendingSubmittals { id displayNumber title status pending canOpen }
      pairableNodeIds
    }
  }
  ${Fragments.CostApplicationFragment}
  ${Fragments.CostAttachmentFragment}
`

export const COST_PENDING_ACTIONS = gql`
  query costPendingActions($projectId: ID!) {
    costPendingActions(projectId: $projectId) {
      type
      id
      state
      number
      title
      contractName
      amount
      since
      dueDate
      byName
    }
  }
`
export const COST_OVERVIEW = gql`
  query costOverview($projectId: ID!, $nodeId: ID) {
    costOverview(projectId: $projectId, nodeId: $nodeId) {
      currency
      totals { revisedBudget committed actual potentialChanges potentialInForecast unallocatedActual projectedFinalCost variance }
      scopes {
        nodeId
        code
        name
        totals { revisedBudget committed actual potentialChanges potentialInForecast projectedFinalCost variance }
      }
      trend { date revisedBudget committed actual projectedFinalCost }
      overruns { nodeId code name revisedBudget projectedFinalCost overrun overrunPercent }
      pending { applications changes invoices overdueInvoices oldestDays }
      billing { warningCount warnings { nodeId code name investorBilled subcontractorBilled difference } }
    }
  }
`

export const COST_BILLING_BY_MONTH = gql`
  query costBillingByMonth($projectId: ID!) {
    costBillingByMonth(projectId: $projectId) {
      months
      investor { amounts total }
      investorUninvoiced
      contracts { id number name counterpartyName amounts total }
      subcontractors { amounts total }
      difference { amounts total }
      cumulative
    }
  }
`

export const COST_RFQS = gql`
  query costRfqs($projectId: ID!, $filter: CostRfqFilter) {
    costRfqs(projectId: $projectId, filter: $filter) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const COST_RFQ = gql`
  query costRfq($id: ID!) {
    costRfq(id: $id) {
      rfq {
        ...CostRfqFragment
      }
      quotes {
        id
        supplierName
        supplierIco
        bidderId
        amount
        receivedAt
        note
        isWinner
        attachments {
          ...CostAttachmentFragment
        }
      }
      attachments {
        ...CostAttachmentFragment
      }
    }
  }
  ${Fragments.CostRfqFragment}
  ${Fragments.CostAttachmentFragment}
`

export const COST_BIDDER_OPTIONS = gql`
  query costBidderOptions {
    bidders {
      id
      name
      deleted
    }
  }
`
export const COST_SUBMITTAL_OPTIONS = gql`
  query costSubmittalOptions($projectId: ID!, $filter: SubmittalFilterInput, $limit: Int) {
    submittals(projectId: $projectId, filter: $filter, limit: $limit) {
      items { id displayNumber title status costContract { id number name } }
      totalCount
    }
  }
`
